import React, { useEffect, useState } from 'react';
import { Coins, Info, Wallet } from 'lucide-react';
import CreditsTab from '../../../components/credits/CreditsTab';
import { getPlatformCreditsRollup } from '../../../services/aiControlApi';
import { api } from '../../../services/apiService';
import type { AiTabProps } from '../AiControlCenter';
import { RubyLoader } from '../../../components/RubyLoader';
import { creditsToRubyTokens } from '../rubyAi/units';

/**
 * The Creator Studio dollar wallet is retired as a way to pay (Task 28): word
 * art, menu boards, compositions and videos charge Ruby Tokens. Its price
 * settings are gone from this page (the stored fields are kept). What is left
 * is read-only: the balances still sitting in it. They are never converted
 * automatically; what happens to them awaits an owner decision.
 */
interface CreatorBalances {
  totalBalance: number;
  walletsWithBalance: number;
}


const usd = (n: number): string => `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(2)}`;
const cents = (n: number): string => `$${(n / 100).toFixed(2)}`;

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-stone-100 last:border-0">
    <dt className="text-xs text-gray-500">{label}</dt>
    <dd className="text-xs font-medium text-gray-900">{value}</dd>
  </div>
);

const CreditsTabPanel: React.FC<AiTabProps> = ({ range, timeRange, onHeaderStats }) => {
  const [creator, setCreator] = useState<CreatorBalances | null>(null);
  const [creatorError, setCreatorError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const rollup = await getPlatformCreditsRollup({
          preset: timeRange.preset, fromIso: range.fromIso, toIso: range.toIso,
        });
        if (!cancelled) {
          onHeaderStats([
            { label: 'Sold', value: usd(rollup.totals.creditsSoldUsd), tone: 'blue' },
            { label: 'Consumed', value: usd(rollup.totals.creditsConsumedUsd), tone: 'white' },
            { label: 'COGS', value: usd(rollup.totals.cogsUsd), tone: 'rose' },
            {
              label: 'Margin',
              value: usd(rollup.totals.grossMarginUsd),
              tone: rollup.totals.grossMarginUsd >= 0 ? 'emerald' : 'rose',
            },
          ]);
        }
      } catch (_) {
        // CreditsTab renders its own error for the same data; a 404 here must
        // not blank the tab. The header simply carries no figures.
      }

      try {
        const report: any = await api.superAdmin.getCreditReports('month');
        if (!cancelled) {
          setCreator({
            totalBalance: Number(report?.summary?.totalBalance) || 0,
            walletsWithBalance: Number(report?.summary?.walletsWithBalance) || 0,
          });
          setCreatorError(null);
        }
      } catch (err: any) {
        if (!cancelled) setCreatorError(err?.message || 'Creator Studio balances unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [range.fromIso, range.toIso, timeRange.preset, onHeaderStats]);

  return (
    <>
      <div className="bg-white border border-stone-200 rounded-xl shadow-sm px-4 py-3 flex items-start gap-2">
        <Info size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-gray-600">
          <strong>One way to pay.</strong> Every Ruby action, Creator Studio
          included, now charges Ruby Tokens. The old Creator Studio dollar wallet
          is no longer spent. Balances left in it are kept as they are and never
          converted automatically — what happens to them is an owner decision.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
        {/* Wallet 1 — agent credit ledger. Reused verbatim: bundles, grant,
            deduct, history and export all already live in CreditsTab. */}
        <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2">
            <Coins size={14} className="text-emerald-600" />
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Agent credit ledger</h2>
              <p className="text-[11px] text-gray-500">POS AI agents · pos-agents-service</p>
            </div>
          </div>
          <div className="p-4">
            <CreditsTab />
          </div>
        </div>

        {/* Wallet 2 — Creator Studio. */}
        <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2">
            <Wallet size={14} className="text-rose-600" />
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Creator Studio wallet</h2>
              <p className="text-[11px] text-gray-500">Retired · balances awaiting conversion</p>
            </div>
          </div>
          <div className="p-4">
            {loading ? (
              <RubyLoader variant="section" />
            ) : creatorError ? (
              <p className="text-xs text-gray-500">{creatorError}</p>
            ) : creator ? (
              <dl className="space-y-2 text-sm">
                <Row label="Creator Studio balance — retired, awaiting an owner decision (not raised automatically yet)" value={cents(creator.totalBalance)} />
                <Row label="Ruby Token equivalent" value={`${creditsToRubyTokens(creator.totalBalance).toLocaleString()} Ruby Tokens`} />
                <Row label="Tenants with a balance" value={String(creator.walletsWithBalance)} />
                <p className="text-[11px] text-gray-500 pt-2 border-t border-stone-100">
                  Read-only. Creator Studio no longer charges this wallet — word art,
                  menu boards, compositions and videos charge Ruby Tokens. Nothing is
                  converted automatically.
                </p>
              </dl>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

export default CreditsTabPanel;
